import { OpenAIMessage } from '@/types/openai';

interface ContextLimits {
  model: string;
  maxTokens: number;
  reserveForCompletion: number;
  maxContextTokens: number;
}

interface TruncationResult {
  truncatedMessages: OpenAIMessage[];
  truncatedKnowledge: any[];
  totalTokens: number;
  truncated: boolean;
  removedItems: number;
}

// Model-specific token limits
const MODEL_LIMITS: Record<string, ContextLimits> = {
  'gpt-4o': {
    model: 'gpt-4o',
    maxTokens: 128000,
    reserveForCompletion: 4000,
    maxContextTokens: 124000
  },
  'gpt-4o-mini': {
    model: 'gpt-4o-mini',
    maxTokens: 128000,
    reserveForCompletion: 2000,
    maxContextTokens: 126000
  },
  'gpt-4.5-preview': {
    model: 'gpt-4.5-preview',
    maxTokens: 200000,
    reserveForCompletion: 4000,
    maxContextTokens: 196000
  }
};

/**
 * Improved token counting function
 */
export function countTokens(text: string, isMultiLanguage: boolean = false): number {
  if (!text || typeof text !== 'string') return 0;
  
  // Base estimation: ~4 characters per token for English
  let baseTokens = Math.ceil(text.length / 4);
  
  // Adjust for non-English languages (typically more tokens per character)
  if (isMultiLanguage) {
    // Asian languages (Chinese, Japanese, Korean) use more tokens
    const asianCharRegex = /[\u4e00-\u9fff\u3040-\u309f\u30a0-\u30ff\uac00-\ud7af]/g;
    const asianCharCount = (text.match(asianCharRegex) || []).length;
    
    if (asianCharCount > 0) {
      // Asian characters typically use 1.5-2 tokens per character
      baseTokens = Math.ceil(text.length / 2.5);
    } else {
      // Other non-English languages (Vietnamese, Thai) use ~1.2x more tokens
      baseTokens = Math.ceil(baseTokens * 1.2);
    }
  }
  
  return baseTokens;
}

/**
 * Calculate total tokens for messages array
 */
export function calculateMessagesTokens(messages: OpenAIMessage[], isMultiLanguage: boolean = false): number {
  return messages.reduce((total, message) => {
    // Add base tokens for message structure
    const baseOverhead = 4; // Role, content wrapper, etc.
    const contentTokens = countTokens(message.content, isMultiLanguage);
    return total + baseOverhead + contentTokens;
  }, 0);
}

/**
 * Calculate tokens for knowledge results
 */
export function calculateKnowledgeTokens(knowledgeResults: any[], isMultiLanguage: boolean = false): number {
  return knowledgeResults.reduce((total, item) => {
    const contentTokens = countTokens(item.content || '', isMultiLanguage);
    const titleTokens = countTokens(item.title || '', isMultiLanguage);
    return total + contentTokens + titleTokens + 10; // Overhead for formatting
  }, 0);
}

/**
 * Get model limits with fallback
 */
export function getModelLimits(model: string): ContextLimits {
  return MODEL_LIMITS[model] || MODEL_LIMITS['gpt-4o-mini'];
}

/**
 * Truncate context to fit within token limits
 */
export function truncateContext(
  messages: OpenAIMessage[],
  knowledgeResults: any[],
  model: string = 'gpt-4o-mini',
  language: string = 'en'
): TruncationResult {
  const isMultiLanguage = language !== 'en';
  const limits = getModelLimits(model);
  
  console.log('[CONTEXT MANAGEMENT] Starting truncation for model:', model);
  
  // Calculate initial token counts
  let messagesTokens = calculateMessagesTokens(messages, isMultiLanguage);
  let knowledgeTokens = calculateKnowledgeTokens(knowledgeResults, isMultiLanguage);
  let totalTokens = messagesTokens + knowledgeTokens;
  
  console.log('[CONTEXT MANAGEMENT] Initial tokens:', {
    messages: messagesTokens,
    knowledge: knowledgeTokens,
    total: totalTokens,
    limit: limits.maxContextTokens
  });
  
  let truncatedMessages = [...messages];
  let truncatedKnowledge = [...knowledgeResults];
  let removedItems = 0;
  let truncated = false;
  
  // If we're within limits, return as-is
  if (totalTokens <= limits.maxContextTokens) {
    return {
      truncatedMessages,
      truncatedKnowledge,
      totalTokens,
      truncated: false,
      removedItems: 0
    };
  }
  
  console.log('[CONTEXT MANAGEMENT] Tokens exceed limit, starting truncation');
  truncated = true;
  
  // Strategy 1: Remove older messages (keep system message and last few user/assistant pairs)
  if (truncatedMessages.length > 5 && totalTokens > limits.maxContextTokens) {
    const systemMessages = truncatedMessages.filter(m => m.role === 'system');
    const otherMessages = truncatedMessages.filter(m => m.role !== 'system');
    
    // Keep last 6 messages (3 user-assistant pairs)
    const recentMessages = otherMessages.slice(-6);
    truncatedMessages = [...systemMessages, ...recentMessages];
    
    messagesTokens = calculateMessagesTokens(truncatedMessages, isMultiLanguage);
    totalTokens = messagesTokens + knowledgeTokens;
    
    console.log('[CONTEXT MANAGEMENT] After message truncation:', {
      messages: messagesTokens,
      total: totalTokens
    });
  }
  
  // Strategy 2: Remove knowledge results with lowest similarity
  while (totalTokens > limits.maxContextTokens && truncatedKnowledge.length > 0) {
    // Sort by similarity and remove the lowest
    truncatedKnowledge.sort((a, b) => (b.similarity || 0) - (a.similarity || 0));
    truncatedKnowledge.pop();
    removedItems++;
    
    knowledgeTokens = calculateKnowledgeTokens(truncatedKnowledge, isMultiLanguage);
    totalTokens = messagesTokens + knowledgeTokens;
    
    console.log('[CONTEXT MANAGEMENT] Removed knowledge item, tokens now:', totalTokens);
  }
  
  // Strategy 3: Truncate individual knowledge content if still too long
  if (totalTokens > limits.maxContextTokens) {
    truncatedKnowledge = truncatedKnowledge.map(item => {
      if (item.content && item.content.length > 1000) {
        return {
          ...item,
          content: item.content.substring(0, 800) + '...'
        };
      }
      return item;
    });
    
    knowledgeTokens = calculateKnowledgeTokens(truncatedKnowledge, isMultiLanguage);
    totalTokens = messagesTokens + knowledgeTokens;
  }
  
  console.log('[CONTEXT MANAGEMENT] Final truncation result:', {
    messages: messagesTokens,
    knowledge: knowledgeTokens,
    total: totalTokens,
    limit: limits.maxContextTokens,
    truncated,
    removedItems
  });
  
  return {
    truncatedMessages,
    truncatedKnowledge,
    totalTokens,
    truncated,
    removedItems
  };
}

/**
 * Validate that context is within safe limits before API call
 */
export function validateContextSize(
  messages: OpenAIMessage[],
  knowledgeResults: any[],
  model: string = 'gpt-4o-mini',
  language: string = 'en'
): { valid: boolean; tokens: number; limit: number; recommendation?: string } {
  const isMultiLanguage = language !== 'en';
  const limits = getModelLimits(model);
  
  const messagesTokens = calculateMessagesTokens(messages, isMultiLanguage);
  const knowledgeTokens = calculateKnowledgeTokens(knowledgeResults, isMultiLanguage);
  const totalTokens = messagesTokens + knowledgeTokens;
  
  const valid = totalTokens <= limits.maxContextTokens;
  
  let recommendation;
  if (!valid) {
    if (knowledgeResults.length > 5) {
      recommendation = 'Reduce knowledge results or increase similarity threshold';
    } else if (messages.length > 10) {
      recommendation = 'Truncate older messages';
    } else {
      recommendation = 'Content is too long, consider chunking';
    }
  }
  
  return {
    valid,
    tokens: totalTokens,
    limit: limits.maxContextTokens,
    recommendation
  };
}
